import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { SplitFlapBoard } from '../../components/split-flap-board/split-flap-board';
import { SplitFlapAudioService } from '../../services/split-flap-audio.service';
import { BoardConfig, BoardMessage, LETTERS_ALPHABET } from '../../models/board.model';

/**
 * One word per message, so the hero board flips them in sequence and loops.
 * A phrase is picked at random per page load, so repeat visitors get variety.
 */
const PREVIEW_PHRASES = [
  ['FLIPPYBOARDS', 'DEPARTING', 'SHORTLY'],
  ['FLIPPYBOARDS', 'ALL', 'ABOARD'],
  ['FLIPPYBOARDS', 'ARRIVALS', 'DEPARTURES', 'WHATEVER'],
];

/**
 * Widest word sets the floor for the narrow layout. Flap size scales as
 * 1/columns, so the wide count is kept just past the longest word - enough
 * blank flaps to read as a departure board, without shrinking the characters.
 */
const NARROW_COLUMNS = 12;
const WIDE_COLUMNS = 16;

/**
 * Tailwind's `sm:` breakpoint, which the bezel around the board is sized by.
 * Matching on the media query rather than window.innerWidth keeps the two in
 * step - innerWidth counts the scrollbar and the media query does not, and that
 * difference is enough to put the wide flap count inside the narrow bezel.
 */
const WIDE_VIEWPORT_QUERY = '(min-width: 640px)';

@Component({
  selector: 'app-landing',
  imports: [RouterLink, SplitFlapBoard],
  templateUrl: './landing.html',
  styleUrl: './landing.css',
})
export class Landing {
  private router = inject(Router);
  private audio = inject(SplitFlapAudioService);

  // The preview words are all letters, so the digits and punctuation on the
  // full drum would only add flip time
  protected readonly previewAlphabet = LETTERS_ALPHABET;

  // Wide board on desktop reads like a departure board; narrow keeps the flaps
  // large enough to be legible on a phone. Blank flaps never flip, so the extra
  // columns cost no animation time.
  private readonly wideViewport = window.matchMedia(WIDE_VIEWPORT_QUERY);
  private readonly columns = signal(this.columnsForViewport());

  constructor() {
    const onViewportChange = () => this.columns.set(this.columnsForViewport());
    this.wideViewport.addEventListener('change', onViewportChange);
    inject(DestroyRef).onDestroy(() =>
      this.wideViewport.removeEventListener('change', onViewportChange),
    );
  }

  protected readonly previewConfig = computed<BoardConfig>(() => ({
    rows: 1,
    columns: this.columns(),
    rotationInterval: 3,
  }));

  // Resolved once per page load rather than inside the computed below, so a
  // window resize cannot swap the phrase out mid-rotation.
  private readonly previewWords =
    PREVIEW_PHRASES[Math.floor(Math.random() * PREVIEW_PHRASES.length)];

  protected readonly previewMessages = computed<BoardMessage[]>(() =>
    this.previewWords.map((word, i) => ({
      id: `preview-${i}`,
      text: this.centered(word, this.columns()),
      order: i + 1,
    })),
  );

  // Silent by default so the page never makes noise unprompted, whatever the
  // visitor's saved global preference is. The board's `muted` input is what
  // enforces that on arrival; the service's mute is the persisted preference.
  protected readonly soundOn = signal(false);

  protected toggleSound() {
    const enabled = !this.soundOn();
    this.soundOn.set(enabled);

    // The saved global mute silences every board regardless of this toggle, so
    // opting in here has to clear it too - otherwise the button would claim
    // sound is on over a board that stays silent.
    this.audio.setMute(!enabled);

    // Browsers keep the audio context suspended until a user gesture
    if (enabled) {
      void this.audio.resumeAudioContext();
    }
  }

  private columnsForViewport(): number {
    return this.wideViewport.matches ? WIDE_COLUMNS : NARROW_COLUMNS;
  }

  /** Pad a word with leading blanks so it sits centered on the board */
  private centered(word: string, columns: number): string {
    return ' '.repeat(Math.max(0, Math.floor((columns - word.length) / 2))) + word;
  }

  signUp() {
    void this.router.navigateByUrl('/signup');
  }

  login() {
    void this.router.navigateByUrl('/login');
  }

  viewDemo() {
    window.open('/demo', '_blank');
  }
}
